/**
 * Bootstrap 4 appends .modal-backdrop to <body>, not inside the modal element, and it is
 * only removed when jQuery's .modal("hide") runs on that same element. If React unmounts
 * the modal first - a route change, a tree swap in App.js while a popup is open - the hide
 * can never run and the backdrop is stranded: a fixed, full-screen, 50% black layer at
 * z-index 1040 that swallows every click. Several of our modals are data-backdrop="static",
 * so clicking it does not dismiss anything either, and the page is dead until a reload.
 *
 * This sweeps up stranded backdrops. It is deliberately conservative: if any modal is still
 * genuinely open, it leaves everything alone.
 */
export const clearOrphanModalBackdrops = (doc) => {
  const targetDoc = doc || (typeof document !== "undefined" ? document : null);

  if (!targetDoc || !targetDoc.body) {
    return 0;
  }

  // A modal that is still open owns its backdrop - not ours to remove.
  if (targetDoc.querySelector(".modal.show")) {
    return 0;
  }

  const backdrops = targetDoc.querySelectorAll(".modal-backdrop");

  if (!backdrops.length) {
    // Body can still be left scroll-locked even when the backdrop went away cleanly.
    releaseBodyScrollLock(targetDoc);
    return 0;
  }

  backdrops.forEach((backdrop) => {
    if (backdrop.parentNode) {
      backdrop.parentNode.removeChild(backdrop);
    }
  });

  releaseBodyScrollLock(targetDoc);

  return backdrops.length;
};

/**
 * Bootstrap also parks `modal-open` and an inline padding-right on <body> while a modal is
 * up; both outlive a stranded backdrop and keep the page unscrollable.
 */
const releaseBodyScrollLock = (targetDoc) => {
  const body = targetDoc.body;

  if (!body) {
    return;
  }

  body.classList.remove("modal-open");

  if (body.style && typeof body.style.removeProperty === "function") {
    body.style.removeProperty("padding-right");
  }
};
